<script lang="ts">
export const selectCls = cva("select disabled:bg-base-100 gap-2", {
	variants: {
		color: {
			default: "",
			neutral: "select-neutral",
			primary: "select-primary",
			secondary: "select-secondary",
			accent: "select-accent",
			info: "select-info",
			warning: "select-warning",
			error: "select-error",
		},
		variant: {
			ghost: "select-ghost",
		},
		size: {
			xs: "select-xs [&_svg]:icon-xs",
			sm: "select-sm [&_svg]:icon-sm",
			md: "select-md [&_svg]:icon-md",
			lg: "select-lg [&_svg]:icon-lg",
		},
	},
});

type _props = VariantProps<typeof selectCls>;

export type InferValue<T extends AcceptableValue, M extends boolean> = M extends true
	? T[]
	: T | null;

export interface SelectProps<T extends AcceptableValue, M extends boolean>
	extends DeprecatedSizeProps,
		FormFieldProps,
		ClassProps,
		Omit<SelectRootProps<T>, "modelValue"> {
	modelValue?: InferValue<T, M>;
	multiple?: M & boolean;
	color?: _props["color"];
	variant?: _props["variant"];
	size?: _props["size"];

	readonly?: boolean;
	disabled?: boolean;
	placeholder?: string;
	bodyLock?: boolean;
	align?: Alignment;
}

export interface SelectEmit<T extends AcceptableValue, M extends boolean>
	extends Omit<SelectRootEmits<T>, "update:modelValue"> {
	"update:modelValue": [value: InferValue<T, M>];
	blur: [];
}
</script>

<script setup lang="ts" generic="T extends AcceptableValue, M extends boolean = false">
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { cva, type VariantProps } from "class-variance-authority";
import {
	SelectContent,
	SelectIcon,
	SelectPortal,
	SelectRoot,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectTrigger,
	SelectValue,
	SelectViewport,
	useForwardPropsEmits,
	type AcceptableValue,
	type SelectRootEmits,
	type SelectRootProps,
} from "reka-ui";
import { useDelegatedProps, type ClassProps } from "../../lib/base-props";
import type { Alignment } from "../../lib/placement";
import type { DeprecatedSizeProps } from "../../lib/size";
import { cn } from "../../lib/utils";
import { type FormFieldProps } from "../form/FormField.vue";
import Icon from "../icon/Icon.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps<SelectProps<T, M>>();
const emit = defineEmits<SelectEmit<T, M>>();

const delegatedProps = useDelegatedProps(props);
const forwarded = useForwardPropsEmits(delegatedProps, emit);

defineSlots<{
	selection(slotProps: { modelValue: InferValue<T, M> }): void;
	prepend(): void;
	append(): void;
}>();

function handleOpenChange(open: boolean) {
	if (!open) {
		emit("blur");
	}
}
</script>

<template>
	<SelectRoot v-bind="forwarded as any" @update:open="handleOpenChange">
		<SelectTrigger :class="cn(selectCls(props))">
			<slot name="prepend" />
			<SelectValue v-slot="slotProps" class="grow text-left" :placeholder="placeholder">
				<slot name="selection" :modelValue="slotProps.modelValue as any" />
			</SelectValue>

			<slot name="append">
				<SelectIcon asChild>
					<Icon :d="mdiChevronDown" />
				</SelectIcon>
			</slot>
		</SelectTrigger>

		<SelectPortal>
			<SelectContent
				as="div"
				class="popover max-h-75 popover-animate border p-0"
				position="popper"
				:align="align ?? 'center'"
				:sideOffset="4"
				positionStrategy="fixed"
				prioritizePosition
				side="bottom"
				updatePositionStrategy="optimized"
				:bodyLock="bodyLock"
			>
				<SelectScrollUpButton
					class="bg-popover z-1 flex-center absolute top-0 w-full cursor-default py-1"
				>
					<Icon size="sm" :d="mdiChevronUp" />
				</SelectScrollUpButton>

				<SelectViewport
					as="div"
					class="w-full scroll-m-1 p-1"
					:style="{
						minWidth: 'var(--reka-select-trigger-width)',
						height: 'var(--reka-select-trigger-width)',
					}"
				>
					<slot />
				</SelectViewport>

				<SelectScrollDownButton
					class="z-1 bg-popover flex-center absolute bottom-0 w-full cursor-default py-1"
				>
					<Icon size="sm" :d="mdiChevronDown" />
				</SelectScrollDownButton>
			</SelectContent>
		</SelectPortal>
	</SelectRoot>
</template>
